import React, { useState, useEffect } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Select } from '@appkit4/react-components/select';
import { Tag } from '@appkit4/react-components/tag';
import { CalendarPicker } from '@appkit4/react-components/datepicker';
import { Checkbox } from '@appkit4/react-components';
import './ExportModal.scss';
import {
    usePostV1TemplatesExtractionExport,
    usePostV1TemplatesGetTemplates,
    useGetV1ProcessProcessIdGetProcessDetails,
    useGetV1DocumentTypeByProcessTypeProcessTypeId,
    usePostV1DocumentsDownloadDocuments,
    useGetV1ProcessParentDocumentsProcessId,
} from '../../../../api/generated/endpoint';
import { Switch } from '@appkit4/react-components/switch';
import { exportPollingController } from '../../../../helpers/useExportPolling';
import useToast from '../../../../helpers/useToast';

interface ExportModalProps {
    visible: boolean;
    onClose: () => void;
    processId: any;
    processTypeId?: number;
    processName?: string;
}

const ExportModal: React.FC<ExportModalProps> = ({
    visible,
    onClose,
    processId,
    processTypeId,
    processName,
}) => {
    const [formData, setFormData] = useState<any>({
        documentTypes: [],
        parentDocs: [],
        template: null,
        date: null,
        documentName: '',
    });
    const showToast = useToast();
    const [exportAll, setExportAll] = useState<boolean>(false);
    const [selectAllDocTypes, setSelectAllDocTypes] = useState(false);
    const [selectAllParentDoc, setSelectAllParentDocs] = useState(true);

    const [availableDocTypes, setAvailableDocTypes] = useState<any[]>([]);
    const [availableParentDocs, setAvailableParentDocs] = useState<any[]>([]);
    const [extractedDocTypes, setExtractedDocTypes] = useState<any[]>([]);
    const [templates, setTemplates] = useState<any[]>([
        { label: 'Default Template', value: null },
    ]);
    const [isLoading, setIsLoading] = useState(false);

    const { data: processData } = useGetV1ProcessProcessIdGetProcessDetails(
        parseInt(processId)
    );

    const { data: documentTypesData } =
        useGetV1DocumentTypeByProcessTypeProcessTypeId(processTypeId || 1);

    const { data: parentDocumentsData } =
        useGetV1ProcessParentDocumentsProcessId(processId);

    const templatesGetMutation = usePostV1TemplatesGetTemplates();

    const { mutate: documentDownloadRequests } =
        usePostV1DocumentsDownloadDocuments();
    const exportMutation = usePostV1TemplatesExtractionExport();

    const fetchTemplates = () => {
        templatesGetMutation.mutate(
            {
                data: {
                    documentTypeIds: [],
                },
            },
            {
                onSuccess: (data: any) => {
                    if (data && data.length > 0) {
                        const templateOptions = data.map((template: any) => ({
                            label: template.templateName,
                            value: template.templateId.toString(),
                        }));

                        setTemplates([
                            { label: 'Default Template', value: null },
                            ...templateOptions,
                        ]);
                    }
                },
                onError: (error: any) => {
                    console.error('Failed to fetch templates:', error);
                },
            }
        );
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    useEffect(() => {
        if (processData && Array.isArray(processData.documentTypesExtracted)) {
            setExtractedDocTypes(
                processData.documentTypesExtracted.map((docType) =>
                    docType.toString()
                )
            );
        }
    }, [processData]);

    useEffect(() => {
        if (
            documentTypesData &&
            parentDocumentsData &&
            extractedDocTypes.length > 0
        ) {
            const docTypes = documentTypesData
                .filter((doc) =>
                    extractedDocTypes.includes(doc.documentTypeName)
                )
                .map((doc: any) => ({
                    label: doc.documentTypeName,
                    value: doc.documentTypeId.toString(),
                }));
            setAvailableDocTypes(docTypes);

            const parentDocsData: any = parentDocumentsData;
            const parentDocs = parentDocsData.processingDocuments.map(
                (doc: any) => ({
                    label: doc.parentDocument,
                    value: doc.parentDocumentId.toString(),
                })
            );
            setAvailableParentDocs(parentDocs);
        }
    }, [documentTypesData, extractedDocTypes, parentDocumentsData]);

    const isFormValid = () => {
        if (processTypeId === 2) {
            return (
                (formData.documentTypes.length > 0 || exportAll) &&
                ((Array.isArray(formData?.date) &&
                    formData?.date?.length === 2) ||
                    formData?.date?.length === 0)
            );
        }
        return (
            (formData.documentTypes.length > 0 || selectAllDocTypes) &&
            // Either date range is selected
            ((Array.isArray(formData?.date) && formData?.date?.length === 2) ||
                // Or date is null/undefined (meaning "All" dates)
                formData?.date?.length === 0)
        );
    };

    const handleSelectAllDocTypes = (checked: boolean) => {
        setSelectAllDocTypes(checked);
        if (checked) {
            setFormData({
                ...formData,
                documentTypes: [...availableDocTypes],
            });
            setAvailableDocTypes([]);
        } else {
            setAvailableDocTypes([
                ...availableDocTypes,
                ...formData.documentTypes,
            ]);
            setFormData({
                ...formData,
                documentTypes: [],
            });
        }
    };

    const handleSelectAllParentDocs = (checked: boolean) => {
        setSelectAllParentDocs(checked);
        if (checked) {
            setFormData({
                ...formData,
                parentDocs: [...availableParentDocs],
            });
            setAvailableParentDocs([]);
        } else {
            setAvailableParentDocs([
                ...availableParentDocs,
                ...formData.parentDocs,
            ]);
            setFormData({
                ...formData,
                parentDocs: [],
            });
        }
    };

    const handleDocumentTypeSelect = (value: any) => {
        const selectedDoc = availableDocTypes.find(
            (doc) => doc.value === value
        );
        if (selectedDoc) {
            setFormData({
                ...formData,
                documentTypes: [...formData.documentTypes, selectedDoc],
            });
            setAvailableDocTypes(
                availableDocTypes.filter((doc) => doc.value !== value)
            );
        }
        setSelectAllDocTypes(false);
    };

    const handleParentDocumentSelect = (value: any) => {
        const selectedDoc = availableParentDocs.find(
            (doc) => doc.value === value
        );
        if (selectedDoc) {
            setFormData({
                ...formData,
                parentDocs: [...formData.parentDocs, selectedDoc],
            });
            setAvailableParentDocs(
                availableParentDocs.filter((doc) => doc.value !== value)
            );
        }
        setSelectAllParentDocs(false);
    };

    const handleRemoveDocType = (value: string) => {
        const docToRemove = formData.documentTypes.find(
            (doc: any) => doc.value === value
        );
        if (docToRemove) {
            setFormData({
                ...formData,
                documentTypes: formData.documentTypes.filter(
                    (doc: any) => doc.value !== value
                ),
            });
            setAvailableDocTypes([...availableDocTypes, docToRemove]);
        }
        setSelectAllDocTypes(false);
    };

    const handleRemoveParentDoc = (value: string) => {
        const docToRemove = formData.parentDocs.find(
            (doc: any) => doc.value === value
        );
        if (docToRemove) {
            setFormData({
                ...formData,
                parentDocs: formData.parentDocs.filter(
                    (doc: any) => doc.value !== value
                ),
            });
            setAvailableParentDocs([...availableDocTypes, docToRemove]);
        }
        setSelectAllParentDocs(false);
    };

    const handleDateChange = (dates: Date[], isValid: boolean) => {
        if (isValid) {
            setFormData({
                ...formData,
                date: dates,
            });
        }
    };

    const handleExportAll = (value: boolean) => {
        setExportAll(value);
    };

    const handleExport = () => {
        if (isFormValid()) {
            setIsLoading(true);

            // Only show toast and start polling for async exports (Extraction - processTypeId=1)
            // For Fill PDF and Online Forms (processTypeId=2,3), downloads are immediate
            if (processTypeId === 1) {
                exportPollingController.start();
                showToast(
                    'Export Started',
                    `You will be notified when complete.`,
                    'success'
                );
            }

            if (processTypeId === 2 || processTypeId === 3) {
                // Get date range
                let startDate = new Date(2025, 0, 1); // Jan 1, 2025
                let endDate = new Date(); // Current date/time

                if (
                    formData.date &&
                    Array.isArray(formData.date) &&
                    formData.date.length === 2
                ) {
                    // Set start to 00:00:00 and end to 23:59:59.999
                    startDate = new Date(formData.date[0]);
                    startDate.setHours(0, 0, 0, 0);
                    endDate = new Date(formData.date[1]);
                    endDate.setHours(23, 59, 59, 999);
                }

                const docDownloadRequest = {
                    documentTypeIds: exportAll
                        ? []
                        : formData.documentTypes.map((doc: any) =>
                              parseInt(doc.value)
                          ),
                    downloadAsZip: true,
                    processType: processTypeId,
                    processId: parseInt(processId),
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                    exportAll: exportAll,
                };

                documentDownloadRequests(
                    { data: docDownloadRequest },
                    {
                        onSuccess: async (response: any) => {
                            const link: any = document.createElement('a');
                            link.href = response.sasUrl;
                            link.download = formData.documentName || 'download';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setIsLoading(false);
                            onClose();
                        },
                        onError: (error) => {
                            console.error('Download failed:', error);
                            setIsLoading(false);
                        },
                    }
                );
            } else {
                // Default dates if date range is not selected
                let fromDate = new Date(2025, 0, 1); // Jan 1, 2025
                let toDate = new Date(); // Current date/time

                // Use selected dates if available
                if (
                    formData.date &&
                    Array.isArray(formData.date) &&
                    formData.date.length === 2
                ) {
                    fromDate = new Date(formData.date[0]);
                    fromDate.setHours(0, 0, 0, 0);
                    toDate = new Date(formData.date[1]);
                    toDate.setHours(23, 59, 59, 999);
                }

                const exportRequest = {
                    processId: parseInt(processId),
                    documentTypeIds: formData.documentTypes.map((doc: any) =>
                        parseInt(doc.value)
                    ),
                    parentDocumentIds: selectAllParentDoc
                        ? availableParentDocs.map((doc: any) =>
                              parseInt(doc.value)
                          )
                        : formData.parentDocs.map((doc: any) =>
                              parseInt(doc.value)
                          ),
                    templateId: formData.template
                        ? parseInt(formData.template)
                        : null,
                    fromDate: fromDate.toISOString(),
                    toDate: toDate.toISOString(),
                    fileName:
                        formData.documentName == ''
                            ? processName
                            : formData.documentName,
                    exportFileName:
                        formData.documentName == ''
                            ? processName
                            : formData.documentName,
                    exportAll: exportAll,
                };

                exportMutation.mutate(
                    { data: exportRequest },
                    {
                        onSuccess: (data: any) => {
                            setIsLoading(false);

                            if (data && data.extractionExportSASUrl) {
                                const downloadLink =
                                    document.createElement('a');
                                downloadLink.href = data.extractionExportSASUrl;
                                downloadLink.target = '_blank';
                                downloadLink.download =
                                    formData.documentName || 'export.xlsx';
                                document.body.appendChild(downloadLink);
                                downloadLink.click();
                                document.body.removeChild(downloadLink);
                            }

                            onClose();
                        },
                        onError: (error) => {
                            console.error('Export failed:', error);
                            setIsLoading(false);
                        },
                    }
                );
            }
        }
    };

    return (
        <Modal
            visible={visible}
            title="Export data"
            ariaLabel="Export Document Modal"
            onCancel={onClose}
            footer={null}
            closable={true}
            className="export-modal"
            modalStyle={{ width: '33.75rem' }}
            footerStyle={{
                paddingTop: '8px',
                marginTop: '-8px',
                minHeight: '64px',
            }}
            bodyStyle={{ minHeight: '92px' }}
            placement="center"
        >
            <div className="export-modal-content">
                <div className="export-form">
                    <div className="form-group">
                        <label>Document Types To Export</label>
                        <Select
                            placeholder="Select"
                            onSelect={handleDocumentTypeSelect}
                            data={availableDocTypes}
                            disabled={selectAllDocTypes}
                        />
                        <div className="select-all-checkbox">
                            <Checkbox
                                checked={selectAllDocTypes}
                                onChange={handleSelectAllDocTypes}
                            >
                                Select All Document Types
                            </Checkbox>
                        </div>
                        <div className="selected-documents">
                            {formData.documentTypes.map((doc: any) => (
                                <Tag
                                    key={doc.value}
                                    onClose={() =>
                                        handleRemoveDocType(doc.value)
                                    }
                                    className="document-tag"
                                >
                                    {doc.label}
                                </Tag>
                            ))}
                        </div>
                        {processTypeId === 1 && (
                            <div>
                                <label>Parent Documents To Export</label>
                                <Select
                                    placeholder="Select"
                                    onSelect={handleParentDocumentSelect}
                                    data={availableParentDocs}
                                    disabled={selectAllParentDoc}
                                />
                                <div className="select-all-checkbox">
                                    <Checkbox
                                        checked={selectAllParentDoc}
                                        onChange={handleSelectAllParentDocs}
                                    >
                                        Select All Parent Documents
                                    </Checkbox>
                                </div>
                                <div className="selected-documents">
                                    {formData.parentDocs.map((doc: any) => (
                                        <Tag
                                            key={doc.value}
                                            onClose={() =>
                                                handleRemoveParentDoc(doc.value)
                                            }
                                            className="document-tag"
                                        >
                                            {doc.label}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    {processTypeId === 1 && (
                        <div className="form-group">
                            <label>Template to Export</label>
                            <Select
                                placeholder="Select Template"
                                value={formData.template}
                                onSelect={(value) =>
                                    setFormData({
                                        ...formData,
                                        template: value,
                                    })
                                }
                                data={templates}
                            />
                        </div>
                    )}
                </div>
                <div className="form-group">
                    <label>Date Range</label>
                    <CalendarPicker
                        value={formData.date || []}
                        onChange={handleDateChange}
                        selectRange={true}
                        placeholder="mm/dd/yyyy to mm/dd/yyyy"
                        locale="en"
                        fieldTitle="All"
                        autoClose={true}
                        editable={true}
                        fieldWidth="100%"
                    />
                </div>

                {processTypeId === 2 ||
                    (processTypeId === 1 && (
                        <div className="form-group">
                            <Switch
                                checked={exportAll}
                                onChange={handleExportAll}
                            >
                                {processTypeId == 1
                                    ? 'Export All'
                                    : 'Include Validation Failed'}
                            </Switch>
                        </div>
                    ))}

                <div className="export-button-container">
                    <Button
                        kind="secondary"
                        onClick={handleExport}
                        className="export-button"
                        disabled={!isFormValid() || isLoading}
                        loading={isLoading}
                    >
                        Export data
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ExportModal;
